import * as Highcharts from "highcharts";
import { PointOptionsObject } from "highcharts";
import { HighchartsChartModule } from "highcharts-angular";

import { isPlatformBrowser } from "@angular/common";
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  PLATFORM_ID,
  computed,
  inject,
  input,
  viewChild,
} from "@angular/core";
import { Router } from "@angular/router";

import { ProductType } from "../../../apis/networkdashboard/graphql/graphql";
import { extendHighcharts } from "../../../helpers/highcharts";
import { phraseDetailPath } from "../../../pages/subscription-detail/subscription-detail-redirection";

interface NetworkgraphPointOptions extends Highcharts.PointOptionsObject {
  id?: string;
  otherSubscriptionId?: string | null;
  customerDescription?: string;
  type?: string;
  vlanrange?: string | null;
}

interface NetworkgraphPoint extends Highcharts.Point {
  id?: string;
  otherSubscriptionId?: string | null;
  options: NetworkgraphPointOptions;
}

interface Topology {
  customerDescription: string;
  otherSubscriptionId?: string | null;
  product?: ProductType | null;
  subscriptionInstanceId: string;
  topologyObjectType: string;
  vlanrange?: string | null;
}

@Component({
  selector: "networkgraph",
  templateUrl: "networkgraph.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [HighchartsChartModule],
})
export class Networkgraph implements AfterViewInit {
  private readonly router = inject(Router);
  private platformId = inject(PLATFORM_ID);

  topology = input.required<Topology[][]>();
  subscription = input.required<{ product: { type: string } }>();

  private chartContainer = viewChild.required<ElementRef>("chartContainer");
  highcharts: typeof Highcharts = Highcharts;
  chartOptions: Highcharts.Options = this.chartInfo; // required
  protected isBrowser = computed(() => isPlatformBrowser(this.platformId));
  private chartRef: Highcharts.Chart | undefined;

  private productType = computed(() => this.subscription().product.type);
  private isFirewall = computed(() => this.productType() == "FW");
  private isIp = computed(() => this.productType() == "IP");
  private isL2vpn = computed(() => this.productType() == "L2VPN");
  private isL3vpn = computed(() => this.productType() == "L3VPN");

  constructor() {
    if (this.isBrowser()) {
      extendHighcharts(this.highcharts);
    }
    this.addStylingEvent();
  }

  get chartInfo(): Highcharts.Options {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;
    return {
      chart: {
        type: "networkgraph",
        height: 800,
        backgroundColor: "transparent",
        style: {
          fontFamily: "Proxima",
        },
      },
      lang: {
        noData: "Retrieving data...",
      },
      title: {
        text: undefined,
      },
      plotOptions: {
        networkgraph: {
          dataLabels: {
            enabled: false,
          },
          keys: ["from", "to"],
          draggable: false,
          layoutAlgorithm: {
            enableSimulation: true,
            maxSpeed: 2000,
            maxIterations: 200,
            linkLength: 24,
          },
          events: {
            click: (e) => {
              if (!e || !e.point) {
                return;
              }

              const point = e.point as NetworkgraphPoint;
              const subscriptionId = point.otherSubscriptionId || point.id;
              if (subscriptionId && subscriptionId.length > 0) {
                const { type: productType, tag: productTag } = e.point["options"]["product"];
                const path = phraseDetailPath(productType, productTag, subscriptionId);
                this.router.navigate(path);
              }
            },
          },
        },
      },
      tooltip: {
        shared: true,
        shadow: false,
        hideDelay: 800,
        outside: true,
        useHTML: true,
        backgroundColor: "transparent",
        borderColor: "transparent",
        formatter() {
          const point = this.point as NetworkgraphPoint;
          const elementId = point.id || "null";
          let element = `<div id="graph-callout-${elementId}" class="graph-callout">`;
          const tooltipTitle = self.makeTooltipTitle(point);
          element += `<h1>${tooltipTitle}</h1>`;
          const tooltipSubtitle = self.makeTooltipSubtitle(point);
          if (tooltipSubtitle) {
            element += `<span>${tooltipSubtitle}</div>`;
          }
          return element;
        },
      },
      exporting: {
        enabled: false,
      },
      credits: {
        enabled: false,
      },
      series: [],
    };
  }

  protected setChartInstance(chart: Highcharts.Chart) {
    this.chartRef = chart;
  }

  ngAfterViewInit(): void {
    this.updateChart();
  }

  async updateChart() {
    const chartData = this.prepareChartSeriesData();
    if (chartData.length === 0) {
      return;
    }

    if (!this.chartRef) {
      return;
    }

    const existingSeries = this.chartRef.get("firewall-tree");
    if (existingSeries) {
      existingSeries.remove();
    }

    this.chartRef.addSeries({
      id: "firewall-tree",
      type: "networkgraph",
      data: chartData,
    });

    this.chartContainer().nativeElement.className = "networkgraph--container__animate";
  }

  private makeTooltipTitle(graphPoint: NetworkgraphPoint) {
    return graphPoint.options.customerDescription ?? graphPoint.options.type ?? "";
  }

  private makeTooltipSubtitle(graphPoint: NetworkgraphPoint) {
    const subtitle = graphPoint.options.vlanrange;
    return subtitle ? `VLAN ${subtitle}` : undefined;
  }

  private addStylingEvent() {
    this.removeStylingEvent();

    Highcharts.addEvent(Highcharts.Series, "afterSetOptions", (e: any) => {
      if (!e || !e.options || e.options.id !== "firewall-tree") {
        return;
      }

      const nodes = {};
      const data = e.options.data || [];
      if (!Array.isArray(data)) {
        return;
      }

      for (const link of data) {
        if (!link.custom) {
          continue;
        }

        const mass = link.custom.from.type === "FIREWALL" ? 100 : link.custom.from.marker.radius;

        if (link.custom.from) {
          nodes[link.from] = {
            ...link.custom.from,
            id: link.from,
            mass,
          };
        }

        if (link.custom.to) {
          nodes[link.to] = {
            ...link.custom.to,
            id: link.to,
            color: link.custom.to.color ? link.custom.to.color : null,
            mass: link.custom.to.marker.radius,
          };
        }
      }

      e.options.nodes = Object.keys(nodes).map((id) => nodes[id]);
    });
  }

  private removeStylingEvent() {
    Highcharts.removeEvent(Highcharts.Series, "afterSetOptions");
  }

  private prepareChartSeriesData(): PointOptionsObject[] {
    return this.topology()
      .filter((nodes) => nodes.length >= 2)
      .map(([from, to]) => ({
        id: from.subscriptionInstanceId || "unknown",
        from: from.subscriptionInstanceId,
        to: to.subscriptionInstanceId,
        name: from.topologyObjectType || "-",
        custom: {
          from: this.stylingForMarker(from),
          to: this.stylingForMarker(to),
        },
      }));
  }

  private stylingForMarker(node: Topology) {
    let radius = 30;
    const topologyObjectType = node.topologyObjectType;

    switch (topologyObjectType) {
      case "FIREWALL":
      case "INTERNET":
      case "L2VPN":
      case "L3VPN":
      case "L3ENDPOINT":
        radius = 34;
        break;
      default:
        radius = 28;
        break;
    }

    if (this.isSelf(node)) {
      radius += 8;
    }

    return {
      color: "yellow",
      name: topologyObjectType,
      type: topologyObjectType,
      vlanrange: node.vlanrange || "",
      otherSubscriptionId: node.otherSubscriptionId,
      customerDescription: node.customerDescription,
      product: node.product,
      marker: {
        radius,
        symbol: `url(/assets/images/networkgraph/icon-${topologyObjectType}.svg)`,
        width: radius * 2,
        height: radius * 2,
      },
    };
  }

  private isSelf(node: Topology) {
    switch (node.topologyObjectType) {
      case "FIREWALL":
        return this.isFirewall();
      case "INTERNET":
        return this.isIp();
      case "L2VPN":
        return this.isL2vpn();
      case "L3VPN":
        return this.isL3vpn();
      default:
        return false;
    }
  }
}
